import { flatMap, head, uniq } from 'lodash'
import { FieldType } from '@wix/wix-data-schema-types'
import type { DefaultField, FieldKeyValuePairs, Fields } from './types'

const supportedFieldTypes: Partial<Record<FieldType, FieldType[]>> = {
  [FieldType.text]: [FieldType.text],
  [FieldType.image]: [FieldType.image],
  [FieldType.richText]: [FieldType.richText, FieldType.text],
}

export const findSupportedFieldTypes = (fields: Record<string, DefaultField>) =>
  uniq(flatMap(fields, ({ type }) => supportedFieldTypes[type]))

const filterAndSortFieldsByType = (
  fields: FieldKeyValuePairs,
  fieldType: FieldType,
) =>
  (supportedFieldTypes[fieldType] || []).reduce<FieldKeyValuePairs>(
    (acc, fieldType) => {
      return [
        ...acc,
        ...fields.filter(([_key, { type }]) => type === fieldType),
      ]
    },
    [],
  )

export const getCandidateFields = ({
  fields,
  supportedFieldTypes,
}: {
  fields: Fields
  supportedFieldTypes: (FieldType | undefined)[]
}) =>
  Object.entries(fields).filter(
    ([_key, { type, isDeleted, systemField }]) =>
      supportedFieldTypes.includes(type) && !isDeleted && !systemField,
  )

export const findRightFieldKey = (
  fields: FieldKeyValuePairs,
  preferableField: DefaultField,
  lessPreferableKeys: string[] = [],
) => {
  const filteredFields = filterAndSortFieldsByType(fields, preferableField.type)
  const fieldKeys = filteredFields.map(([key]) => key)

  if (fieldKeys.length <= 1) {
    return head(fieldKeys)
  }

  const preferableKey = preferableField.fieldKey

  if (fieldKeys.some((key: string) => key === preferableKey)) {
    return preferableKey
  }

  const ordinaryKey = fieldKeys.find(
    (key: string) => !lessPreferableKeys.includes(key),
  )

  return ordinaryKey || head(fieldKeys)
}
